<template>
  <div>
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="d-flex align-center justify-space-between pa-4">
            <span>Test Case Configurations</span>
            <div class="d-flex ga-2">
              <VBtn
                color="info"
                size="small"
                variant="outlined"
                :loading="isCreatingPR"
                @click="createPR"
              >
                <VIcon
                  start
                  icon="ri-git-pull-request-line"
                />
                Create PR
              </VBtn>
              <VBtn
                color="primary"
                size="small"
                @click="openAddDialog"
              >
                <VIcon
                  start
                  icon="ri-add-line"
                />
                Add Config
              </VBtn>
            </div>
          </VCardTitle>

          <!-- Filters -->
          <VCardText class="pb-0">
            <VRow dense>
              <VCol
                cols="12"
                md="3"
              >
                <VTextField
                  v-model="searchQuery"
                  placeholder="Search..."
                  density="compact"
                  prepend-inner-icon="ri-search-line"
                  clearable
                  hide-details
                />
              </VCol>
              <VCol
                cols="12"
                md="2"
              >
                <VSelect
                  v-model="filterCategory"
                  :items="categoryOptions"
                  label="Category"
                  density="compact"
                  clearable
                  hide-details
                />
              </VCol>
              <VCol
                cols="12"
                md="2"
              >
                <VSelect
                  v-model="filterTestType"
                  :items="testTypeOptions"
                  label="Test Type"
                  density="compact"
                  clearable
                  hide-details
                  multiple
                />
              </VCol>
            </VRow>
          </VCardText>

          <!-- Sheet-style Table -->
          <div
            class="table-responsive"
            style="overflow-x: auto"
          >
            <VTable
              density="compact"
              class="text-no-wrap sheet-table"
            >
              <thead>
                <tr class="bg-grey-lighten-4">
                  <th
                    rowspan="3"
                    class="sticky-col"
                  >
                    NO
                  </th>
                  <th
                    rowspan="3"
                    class="sticky-col-2"
                  >
                    ID
                  </th>
                  <th rowspan="3">Category</th>
                  <th
                    rowspan="3"
                    style="min-width: 300px"
                  >
                    Test Case
                  </th>
                  <th rowspan="3">Type</th>
                  <th
                    :colspan="4"
                    class="text-center border-left"
                  >
                    Reservation Action
                  </th>
                  <th
                    :colspan="apiVendors.length + webviewVendors.length"
                    class="text-center border-left"
                  >
                    Vendors
                  </th>
                  <th
                    :colspan="inventoryList.length"
                    class="text-center border-left"
                  >
                    Inventory
                  </th>
                  <th
                    rowspan="3"
                    class="border-left"
                  >
                    Pricing Type
                  </th>
                  <th rowspan="3">Payment Type</th>
                  <th
                    rowspan="3"
                    class="text-center"
                  >
                    Actions
                  </th>
                </tr>
                <tr class="bg-grey-lighten-4">
                  <!-- Action sub-headers -->
                  <th
                    rowspan="2"
                    class="text-center border-left sub-header"
                  >
                    Create
                  </th>
                  <th
                    rowspan="2"
                    class="text-center sub-header"
                  >
                    Update
                  </th>
                  <th
                    rowspan="2"
                    class="text-center sub-header"
                  >
                    Cancel
                  </th>
                  <th
                    rowspan="2"
                    class="text-center sub-header"
                  >
                    Force
                  </th>
                  <!-- Vendor sub-group: API -->
                  <th
                    :colspan="apiVendors.length"
                    class="text-center border-left"
                  >
                    API
                  </th>
                  <!-- Vendor sub-group: Webview -->
                  <th
                    :colspan="webviewVendors.length"
                    class="text-center border-left"
                  >
                    Webview
                  </th>
                  <!-- Inventory sub-headers -->
                  <th
                    v-for="inv in inventoryList"
                    :key="'ih-' + inv.id"
                    rowspan="2"
                    class="text-center sub-header"
                    :class="{ 'border-left': inv === inventoryList[0] }"
                  >
                    <VTooltip location="top">
                      <template #activator="{ props }">
                        <span
                          v-bind="props"
                          class="sub-header-text"
                          >{{ inv.aliasName || inv.name }}</span
                        >
                      </template>
                      {{ inv.aliasName || inv.name }}
                    </VTooltip>
                  </th>
                </tr>
                <tr class="bg-grey-lighten-4">
                  <!-- API Vendor name sub-headers -->
                  <th
                    v-for="vendor in apiVendors"
                    :key="'vh-api-' + vendor.id"
                    class="text-center sub-header"
                    :class="{ 'border-left': vendor === apiVendors[0] }"
                  >
                    <VTooltip location="top">
                      <template #activator="{ props }">
                        <span
                          v-bind="props"
                          class="sub-header-text"
                          >{{ vendor.aliasName || vendor.name }}</span
                        >
                      </template>
                      {{ vendor.aliasName || vendor.name }}
                    </VTooltip>
                  </th>
                  <!-- Webview Vendor name sub-headers -->
                  <th
                    v-for="vendor in webviewVendors"
                    :key="'vh-wv-' + vendor.id"
                    class="text-center sub-header"
                    :class="{ 'border-left': vendor === webviewVendors[0] }"
                  >
                    <VTooltip location="top">
                      <template #activator="{ props }">
                        <span
                          v-bind="props"
                          class="sub-header-text"
                          >{{ vendor.aliasName || vendor.name }}</span
                        >
                      </template>
                      {{ vendor.aliasName || vendor.name }}
                    </VTooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td
                    :colspan="totalColumns"
                    class="text-center pa-6"
                  >
                    <VProgressCircular
                      indeterminate
                      color="primary"
                    />
                  </td>
                </tr>
                <tr v-else-if="filteredConfigs.length === 0">
                  <td
                    :colspan="totalColumns"
                    class="text-center pa-6 text-grey"
                  >
                    No test case configs found. Click "Add Config" to create one.
                  </td>
                </tr>
                <tr
                  v-for="(config, index) in filteredConfigs"
                  :key="config.id"
                  class="config-row"
                >
                  <!-- NO -->
                  <td class="sticky-col text-center">{{ index + 1 }}</td>
                  <!-- ID -->
                  <td class="sticky-col-2 font-weight-medium">{{ config.testCases?.name || '-' }}</td>
                  <!-- Category -->
                  <td>
                    <VChip
                      size="x-small"
                      :color="getCategoryColor(config.category)"
                      variant="tonal"
                    >
                      {{ config.category }}
                    </VChip>
                  </td>
                  <!-- Test Case Description -->
                  <td style="min-width: 300px; white-space: normal">{{ config.description || '-' }}</td>
                  <!-- Type -->
                  <td>
                    <VChip
                      size="x-small"
                      v-for="t in config.testTypes || []"
                      :key="t"
                      :color="getTestTypeColor(t)"
                      variant="tonal"
                      class="mr-1"
                    >
                      {{ t }}
                    </VChip>
                  </td>
                  <!-- Actions: Create/Update/Cancel/Force -->
                  <td class="text-center border-left">
                    <VIcon
                      :icon="config.actionCreate ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'"
                      :color="config.actionCreate ? 'success' : 'grey-lighten-1'"
                      size="20"
                    />
                  </td>
                  <td class="text-center">
                    <VIcon
                      :icon="config.actionUpdate ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'"
                      :color="config.actionUpdate ? 'success' : 'grey-lighten-1'"
                      size="20"
                    />
                  </td>
                  <td class="text-center">
                    <VIcon
                      :icon="config.actionDelete ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'"
                      :color="config.actionDelete ? 'success' : 'grey-lighten-1'"
                      size="20"
                    />
                  </td>
                  <td class="text-center">
                    <VIcon
                      :icon="config.actionForceCancel ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'"
                      :color="config.actionForceCancel ? 'success' : 'grey-lighten-1'"
                      size="20"
                    />
                  </td>
                  <!-- API Vendor columns -->
                  <td
                    v-for="vendor in apiVendors"
                    :key="'v-api-' + vendor.id + '-' + config.id"
                    class="text-center"
                    :class="{ 'border-left': vendor === apiVendors[0] }"
                  >
                    <VIcon
                      :icon="isVendorEnabled(config, vendor.id) ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'"
                      :color="isVendorEnabled(config, vendor.id) ? 'info' : 'grey-lighten-1'"
                      size="20"
                    />
                  </td>
                  <!-- Webview Vendor columns -->
                  <td
                    v-for="vendor in webviewVendors"
                    :key="'v-wv-' + vendor.id + '-' + config.id"
                    class="text-center"
                    :class="{ 'border-left': vendor === webviewVendors[0] }"
                  >
                    <VIcon
                      :icon="isVendorEnabled(config, vendor.id) ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'"
                      :color="isVendorEnabled(config, vendor.id) ? 'warning' : 'grey-lighten-1'"
                      size="20"
                    />
                  </td>
                  <!-- Inventory columns -->
                  <td
                    v-for="inv in inventoryList"
                    :key="'i-' + inv.id + '-' + config.id"
                    class="text-center"
                    :class="{ 'border-left': inv === inventoryList[0] }"
                  >
                    <VIcon
                      :icon="isInventoryEnabled(config, inv.id) ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'"
                      :color="isInventoryEnabled(config, inv.id) ? 'success' : 'grey-lighten-1'"
                      size="20"
                    />
                  </td>
                  <!-- Pricing -->
                  <td class="border-left">{{ config.pricingTypes?.displayName || 'Any' }}</td>
                  <!-- Payment -->
                  <td>{{ config.paymentTypes?.displayName || 'Any' }}</td>
                  <!-- Actions -->
                  <td class="text-center">
                    <VBtn
                      icon
                      size="x-small"
                      variant="text"
                      @click="openEditDialog(config)"
                    >
                      <VIcon
                        icon="ri-pencil-line"
                        size="16"
                      />
                    </VBtn>
                    <VBtn
                      icon
                      size="x-small"
                      variant="text"
                      color="error"
                      @click="openDeleteDialog(config)"
                    >
                      <VIcon
                        icon="ri-delete-bin-5-line"
                        size="16"
                      />
                    </VBtn>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </div>
        </VCard>
      </VCol>
    </VRow>

    <!-- Add/Edit Dialog -->
    <VDialog
      v-model="showFormDialog"
      max-width="900"
      persistent
      scrollable
    >
      <VCard :title="isEditMode ? 'Edit Test Case Config' : 'Add Test Case Config'">
        <VCardText style="max-height: 70vh; overflow-y: auto">
          <VForm ref="formRef">
            <VRow>
              <VCol
                v-if="!isEditMode"
                cols="12"
                md="8"
              >
                <div class="d-flex ga-2 align-start">
                  <VTextField
                    model-value="tc-"
                    label="Prefix"
                    disabled
                    style="max-width: 70px"
                  />
                  <VTextField
                    v-model.number="tcNumber"
                    label="Number *"
                    type="number"
                    placeholder="089"
                    :rules="[
                      v => !!v || 'Required',
                      v => (Number.isInteger(Number(v)) && Number(v) > 0) || 'Must be a positive integer',
                    ]"
                    style="max-width: 120px"
                  />
                  <VTextField
                    v-model="tcDescription"
                    label="Description *"
                    placeholder="voucher-perperson-prepaid"
                    :rules="[
                      v => !!v || 'Required',
                      v => /^[a-zA-Z0-9-]+$/.test(v) || 'Use only letters, numbers, and hyphens',
                    ]"
                    hint="Final name: {{ computedTestCaseName }}"
                    persistent-hint
                  />
                </div>
              </VCol>
              <VCol
                v-else
                cols="12"
                md="6"
              >
                <VTextField
                  :model-value="formData.testCaseName"
                  label="Test Case Name"
                  disabled
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VTextField
                  v-model="formData.category"
                  label="Category *"
                  :rules="[v => !!v || 'Required']"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="formData.description"
                  label="Test Case Description"
                />
              </VCol>
              <VCol
                cols="12"
                md="4"
              >
                <VSelect
                  v-model="formData.testTypes"
                  :items="testTypeOptions"
                  label="Test Types"
                  multiple
                  chips
                />
              </VCol>
              <VCol
                cols="12"
                md="4"
              >
                <VSelect
                  v-model="formData.pricingTypeId"
                  :items="pricingTypeItems"
                  item-title="displayName"
                  item-value="id"
                  label="Pricing Type"
                  clearable
                />
              </VCol>
              <VCol
                cols="12"
                md="4"
              >
                <VSelect
                  v-model="formData.paymentTypeId"
                  :items="paymentTypeItems"
                  item-title="displayName"
                  item-value="id"
                  label="Payment Type"
                  clearable
                />
              </VCol>
              <VCol cols="12">
                <div class="text-subtitle-2 mb-2">Reservation Actions</div>
                <div class="d-flex ga-4">
                  <VCheckbox
                    v-model="formData.actionCreate"
                    label="Create"
                    density="compact"
                  />
                  <VCheckbox
                    v-model="formData.actionUpdate"
                    label="Update"
                    density="compact"
                  />
                  <VCheckbox
                    v-model="formData.actionDelete"
                    label="Cancel"
                    density="compact"
                  />
                  <VCheckbox
                    v-model="formData.actionForceCancel"
                    label="Force Cancel"
                    density="compact"
                  />
                </div>
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VTextField
                  v-model.number="formData.adultCount"
                  label="Adults"
                  type="number"
                  min="0"
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VTextField
                  v-model.number="formData.kidsCount"
                  label="Kids"
                  type="number"
                  min="0"
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VSelect
                  v-model="formData.customerType"
                  :items="['random', 'valid', 'invalid_phone']"
                  label="Customer Type"
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VSelect
                  v-model="formData.webviewPaymentMethod"
                  :items="[
                    { title: 'None', value: null },
                    { title: 'PromptPay', value: 'promptpay' },
                    { title: 'Credit Card', value: 'credit_card' },
                    { title: 'Alipay', value: 'alipay' },
                    { title: 'WeChat', value: 'wechat' },
                  ]"
                  label="Webview Payment"
                  clearable
                />
              </VCol>
              <VCol cols="12">
                <div class="text-subtitle-2 mb-2">API Vendors</div>
                <div class="d-flex flex-wrap ga-2">
                  <VCheckbox
                    v-for="vendor in apiVendors"
                    :key="vendor.id"
                    v-model="formData.vendors[vendor.id]"
                    :label="vendor.aliasName || vendor.name"
                    density="compact"
                    class="flex-grow-0"
                  />
                </div>
              </VCol>
              <VCol cols="12">
                <div class="text-subtitle-2 mb-2">Webview Vendors</div>
                <div class="d-flex flex-wrap ga-2">
                  <VCheckbox
                    v-for="vendor in webviewVendors"
                    :key="vendor.id"
                    v-model="formData.vendors[vendor.id]"
                    :label="vendor.aliasName || vendor.name"
                    density="compact"
                    class="flex-grow-0"
                  />
                </div>
              </VCol>
              <VCol cols="12">
                <div class="text-subtitle-2 mb-2">Inventory Sources</div>
                <div class="d-flex flex-wrap ga-2">
                  <VCheckbox
                    v-for="inv in inventoryList"
                    :key="inv.id"
                    v-model="formData.inventorySources[inv.id]"
                    :label="inv.aliasName || inv.name"
                    density="compact"
                    class="flex-grow-0"
                  />
                </div>
              </VCol>
              <VCol cols="12">
                <div class="text-subtitle-2 mb-2">Countries</div>
                <div class="d-flex flex-wrap ga-2">
                  <VCheckbox
                    v-for="country in allCountries"
                    :key="country.id"
                    v-model="formData.countries[country.id]"
                    :label="country.name"
                    density="compact"
                    class="flex-grow-0"
                    disabled
                  />
                </div>
              </VCol>
              <VCol cols="12">
                <div class="text-subtitle-2 mb-2">Options</div>
                <div class="d-flex flex-wrap ga-4">
                  <VSwitch
                    v-model="formData.validateMemo"
                    label="Validate Memo"
                    color="primary"
                    density="compact"
                  />
                  <VSwitch
                    v-model="formData.isUsingAutomationHeader"
                    label="Automation Header"
                    color="warning"
                    density="compact"
                  />
                  <VSwitch
                    v-model="formData.disableMasterDataSearch"
                    label="Disable Master Data"
                    color="error"
                    density="compact"
                  />
                </div>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
        <VCardActions class="pa-4">
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="closeFormDialog"
            :disabled="isSaving"
            >Cancel</VBtn
          >
          <VBtn
            color="primary"
            @click="handleSubmit"
            :loading="isSaving"
          >
            {{ isEditMode ? 'Update' : 'Create' }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Delete Confirmation -->
    <VDialog
      v-model="showDeleteDialog"
      max-width="440"
    >
      <VCard>
        <VCardTitle>Delete Test Case Config</VCardTitle>
        <VCardText>
          Are you sure you want to delete the config for
          <strong>"{{ configToDelete?.testCases?.name }}"</strong>?
          <div class="text-caption text-error mt-2">This action cannot be undone.</div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="showDeleteDialog = false"
            :disabled="isDeleting"
            >Cancel</VBtn
          >
          <VBtn
            color="error"
            @click="confirmDelete"
            :loading="isDeleting"
            >Delete</VBtn
          >
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Snackbar -->
    <VSnackbar
      v-model="isShowSnackBar"
      :color="alertType"
      multi-line
    >
      {{ alertMessage }}
    </VSnackbar>
  </div>
</template>

<script setup lang="ts">
import { useAlert } from '@/composables/useAlert'
import { useConfigStore } from '@/stores/config'
import { useFetch } from '@vueuse/core'

const configStore = useConfigStore()
const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()

const configs = ref<any[]>([])
const paymentTypeItems = ref<any[]>([])
const pricingTypeItems = ref<any[]>([])
const allCountries = ref<any[]>([])
const vendorList = computed(() => configStore.vendors || [])
const apiVendors = computed(() => vendorList.value.filter((v: any) => v.vendorType === 'api'))
const webviewVendors = computed(() => vendorList.value.filter((v: any) => v.vendorType === 'webview'))
const inventoryList = computed(() => configStore.inventorySources || [])
const isLoading = ref(false)
const isSaving = ref(false)
const isDeleting = ref(false)
const isCreatingPR = ref(false)

const totalColumns = computed(
  () => 9 + apiVendors.value.length + webviewVendors.value.length + inventoryList.value.length + 3,
)

const searchQuery = ref('')
const filterCategory = ref<string | null>(null)
const filterTestType = ref<string[] | null>(null)
const testTypeOptions = ['api', 'webview']

const categoryOptions = computed(() => {
  const cats = [...new Set(configs.value.map(c => c.category).filter(Boolean))]
  return cats.sort()
})

const filteredConfigs = computed(() => {
  return configs.value.filter(config => {
    const name = config.testCases?.name?.toLowerCase() || ''
    const desc = config.description?.toLowerCase() || ''
    const query = searchQuery.value?.toLowerCase() || ''
    if (query && !name.includes(query) && !desc.includes(query)) return false
    if (filterCategory.value && config.category !== filterCategory.value) return false
    if (filterTestType.value && filterTestType.value.length > 0) {
      const configTypes = config.testTypes || []
      if (!filterTestType.value.some((ft: string) => configTypes.includes(ft))) return false
    }
    return true
  })
})

const isVendorEnabled = (config: any, vendorId: number) =>
  (config.testCaseVendors || []).some((v: any) => v.vendors?.id === vendorId && v.enabled)

const isInventoryEnabled = (config: any, invId: number) =>
  (config.testCaseSuppliers || []).some((i: any) => i.suppliers?.id === invId && i.enabled)

const showFormDialog = ref(false)
const isEditMode = ref(false)
const formRef = ref<any>(null)
const formData = ref(getDefaultFormData())
const tcNumber = ref<number | null>(null)
const tcDescription = ref('')

const computedTestCaseName = computed(() => {
  if (!tcNumber.value || !tcDescription.value) return ''
  const paddedNum = String(tcNumber.value).padStart(3, '0')
  return `tc-${paddedNum}-${tcDescription.value}`
})

function getDefaultFormData() {
  return {
    id: null as number | null,
    testCaseId: null as number | null,
    testCaseName: '',
    category: '',
    testTypes: ['api'],
    description: '',
    actionCreate: true,
    actionUpdate: false,
    actionDelete: false,
    actionForceCancel: true,
    pricingTypeId: null as number | null,
    paymentTypeId: null as number | null,
    webviewPaymentMethod: null as string | null,
    adultCount: 2,
    kidsCount: 0,
    customerType: 'random',
    isUsingAutomationHeader: false,
    disableMasterDataSearch: false,
    validateMemo: true,
    enabled: true,
    vendors: {} as Record<number, boolean>,
    inventorySources: {} as Record<number, boolean>,
    countries: {} as Record<number, boolean>,
  }
}

const showDeleteDialog = ref(false)
const configToDelete = ref<any>(null)

const fetchConfigs = async () => {
  isLoading.value = true
  try {
    const { data } = await useFetch('/api/setting/test-case-configs')
    const parsed = JSON.parse(data.value as string)
    if (!parsed.success) throw new Error(parsed.message)
    configs.value = parsed.data || []
  } catch (err: any) {
    showAlert(err.message || 'Error fetching configs', 'error')
  } finally {
    isLoading.value = false
  }
}

const fetchLookups = async () => {
  try {
    const { data } = await useFetch('/api/setting/master-data-lookups')
    const parsed = JSON.parse(data.value as string)
    if (parsed.success) {
      paymentTypeItems.value = parsed.data.paymentTypes || []
      pricingTypeItems.value = parsed.data.pricingTypes || []
      allCountries.value = parsed.data.countries || []
    }
  } catch (err) {
    console.error('Error fetching lookups:', err)
  }
}

const openAddDialog = () => {
  isEditMode.value = false
  formData.value = getDefaultFormData()
  tcNumber.value = null
  tcDescription.value = ''
  // Auto-check Thailand
  setThailandChecked()
  showFormDialog.value = true
}

const openEditDialog = (config: any) => {
  isEditMode.value = true
  formData.value = {
    id: config.id,
    testCaseId: config.testCaseId || config.testCases?.id,
    testCaseName: config.testCases?.name || '',
    category: config.category,
    testTypes: config.testTypes || ['api'],
    description: config.description || '',
    actionCreate: config.actionCreate,
    actionUpdate: config.actionUpdate,
    actionDelete: config.actionDelete,
    actionForceCancel: config.actionForceCancel,
    pricingTypeId: config.pricingTypeId || config.pricingTypes?.id || null,
    paymentTypeId: config.paymentTypeId || config.paymentTypes?.id || null,
    webviewPaymentMethod: config.webviewPaymentMethod,
    adultCount: config.adultCount,
    kidsCount: config.kidsCount,
    customerType: config.customerType || 'random',
    isUsingAutomationHeader: config.isUsingAutomationHeader,
    disableMasterDataSearch: config.disableMasterDataSearch,
    validateMemo: config.validateMemo,
    enabled: config.enabled ?? true,
    vendors: buildBooleanMap(config.testCaseVendors, 'vendors'),
    inventorySources: buildBooleanMap(config.testCaseSuppliers, 'suppliers'),
    countries: buildBooleanMap(config.testCaseCountries, 'countries'),
  }
  // Ensure Thailand is always checked
  setThailandChecked()
  showFormDialog.value = true
}

function setThailandChecked() {
  const thailand = allCountries.value.find((c: any) => c.name?.toLowerCase() === 'thailand')
  if (thailand) {
    formData.value.countries[thailand.id] = true
  }
}

const closeFormDialog = () => {
  showFormDialog.value = false
  formData.value = getDefaultFormData()
}

const openDeleteDialog = (config: any) => {
  configToDelete.value = config
  showDeleteDialog.value = true
}

function buildBooleanMap(junctions: any[], relKey: string): Record<number, boolean> {
  const map: Record<number, boolean> = {}
  if (!junctions) return map
  for (const j of junctions) {
    const relatedId = j[relKey]?.id
    if (relatedId) map[relatedId] = j.enabled
  }
  return map
}

function mapToArray(boolMap: Record<number, boolean>, idKey: string) {
  return Object.entries(boolMap)
    .filter(([, enabled]) => enabled)
    .map(([id]) => ({ [idKey]: parseInt(id), enabled: true }))
}

const handleSubmit = async () => {
  // Compose testCaseName from parts when creating
  if (!isEditMode.value) {
    formData.value.testCaseName = computedTestCaseName.value
  }

  if (!isEditMode.value && !formData.value.testCaseName) {
    showAlert('Test case number and description are required.', 'error')
    return
  }
  if (!formData.value.category) {
    showAlert('Category is required.', 'error')
    return
  }

  isSaving.value = true
  try {
    const payload = {
      ...formData.value,
      vendors: mapToArray(formData.value.vendors, 'vendorSourceId'),
      inventorySources: mapToArray(formData.value.inventorySources, 'inventorySourceId'),
      countries: mapToArray(formData.value.countries, 'countryId'),
    }

    const method = isEditMode.value ? 'PUT' : 'POST'
    const response = (await $fetch('/api/setting/test-case-configs', {
      method,
      body: payload,
      headers: { 'Content-Type': 'application/json' },
    })) as { success: boolean; message?: string }

    if (!response.success) throw new Error(response.message)

    showAlert(response.message || 'Saved successfully.', 'success')

    // Auto-sync spec file to GitHub repo
    const testCaseName = formData.value.testCaseName
    if (testCaseName) {
      try {
        const syncResponse = (await $fetch('/api/setting/sync-spec-file', {
          method: 'POST',
          body: { testCaseName },
          headers: { 'Content-Type': 'application/json' },
        })) as any

        if (syncResponse?.success && syncResponse.action !== 'skipped') {
          let msg = `Saved & spec file ${syncResponse.action} in GitHub repo.`
          if (syncResponse.pr?.prUrl) {
            msg += ` PR: ${syncResponse.pr.prUrl}`
          }
          showAlert(msg, 'success')
        }
      } catch (syncErr: any) {
        console.warn('Spec file sync failed (non-blocking):', syncErr.message)
      }
    }

    await fetchConfigs()
    closeFormDialog()
  } catch (err: any) {
    showAlert(err.message || 'Error saving config.', 'error')
  } finally {
    isSaving.value = false
    setTimeout(clearAlert, 3000)
  }
}

const confirmDelete = async () => {
  if (!configToDelete.value?.id) return
  isDeleting.value = true
  try {
    const response = (await $fetch('/api/setting/test-case-configs', {
      method: 'DELETE',
      body: { id: configToDelete.value.id },
      headers: { 'Content-Type': 'application/json' },
    })) as { success: boolean; message?: string; testCaseName?: string }

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete test case config')
    }

    showAlert(response.message || 'Deleted successfully.', 'success')

    // Auto-sync: delete spec file from GitHub + create PR
    if (response.testCaseName) {
      try {
        const syncResponse = (await $fetch('/api/setting/sync-spec-file', {
          method: 'POST',
          body: { testCaseName: response.testCaseName, action: 'delete' },
          headers: { 'Content-Type': 'application/json' },
        })) as any

        if (syncResponse?.success && syncResponse.action !== 'skipped') {
          let msg = `Deleted & spec file removed from GitHub.`
          if (syncResponse.pr?.prUrl) {
            msg += ` PR: ${syncResponse.pr.prUrl}`
          }
          showAlert(msg, 'success')
        }
      } catch (syncErr: any) {
        console.warn('Spec file delete sync failed (non-blocking):', syncErr.message)
      }
    }

    await fetchConfigs()
  } catch (err: any) {
    showAlert(err.message || 'Error deleting config.', 'error')
  } finally {
    isDeleting.value = false
    showDeleteDialog.value = false
    configToDelete.value = null
    setTimeout(clearAlert, 3000)
  }
}

const createPR = async () => {
  isCreatingPR.value = true
  try {
    const response = (await $fetch('/api/setting/create-pr', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })) as any

    if (!response?.success) throw new Error(response?.message)

    let msg = response.message
    if (response.prUrl) {
      msg += ` → ${response.prUrl}`
    }
    showAlert(msg, 'success')
  } catch (err: any) {
    showAlert(err.message || 'Error creating PR.', 'error')
  } finally {
    isCreatingPR.value = false
    setTimeout(clearAlert, 5000)
  }
}

const getCategoryColor = (cat: string) => {
  const colors: Record<string, string> = {
    'pax-qty': 'primary',
    'phone-number': 'warning',
    inventory: 'info',
    pricing: 'error',
    'multiple-booking': 'secondary',
    'mix-n-match': 'success',
    'get-your-guide': 'deep-purple',
    'dianping-voucher': 'orange',
    'dynamic-pricing': 'teal',
    'automation-header': 'brown',
    reservation: 'indigo',
    package: 'cyan',
  }
  return colors[cat] || 'default'
}

const getTestTypeColor = (type: string) => ({ api: 'primary', webview: 'warning' })[type] || 'default'

onMounted(async () => {
  await configStore.initConfig()
  await Promise.all([fetchConfigs(), fetchLookups()])
})
</script>

<style scoped>
.sheet-table th,
.sheet-table td {
  padding: 4px 8px !important;
  font-size: 0.8rem;
}

.sheet-table .sub-header {
  font-size: 0.7rem;
  font-weight: 500;
  writing-mode: vertical-lr;
  text-orientation: mixed;
  white-space: nowrap;
  padding: 8px 4px !important;
  min-width: 36px;
  max-width: 36px;
  height: 100px;
  overflow: hidden;
}

.sub-header-text {
  display: inline-block;
  max-height: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.border-left {
  border-left: 2px solid rgba(var(--v-border-color), 0.2) !important;
}

.config-row:hover {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}

.sticky-col-2 {
  position: sticky;
  left: 40px;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}
</style>
